import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph, MORPH_OPTIONS } from "@theme/morph";
import {
  yieldToMainThread,
  getViewParameterValue,
  ResizeNotifier,
} from "@theme/utilities";

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /**
   * Color substitution groups.
   * Colors within the same group are considered acceptable alternatives for each other.
   * All comparisons are case-insensitive. Colors NOT listed here must match exactly.
   */
  static #COLOR_GROUPS = [
    ["Navy Blue", "Dark Blue", "Brownish Navy", "Blue Black", "Royal Blue", "Gitan"],
    ["Charcoal", "Anthracite"],
    ["Gray", "Light Gray"],
  ];

  /**
   * Returns the list of acceptable color names (lowercase) for the given color.
   * If the color belongs to a group, the whole group is returned.
   * Otherwise returns a single-element array with the exact color (lowercase).
   * @param {string} color
   * @returns {string[]}
   */
  static #getAcceptableColors(color) {
    const lower = color.toLowerCase();
    for (const group of VariantPicker.#COLOR_GROUPS) {
      if (group.some(c => c.toLowerCase() === lower)) {
        return group.map(c => c.toLowerCase());
      }
    }
    return [lower];
  }

  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLElement | null} */
  #searchPopup = null;

  /**
   * Lazy cache: resolves to { colorMap: Map<localizedColor, englishColor>, productType: string }
   * Fetched once from the default-locale /products/handle.json.
   * @type {Promise<{colorMap: Map<string,string>, productType: string}> | null}
   */
  #englishProductDataPromise = null;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll("input") ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.dataset.currentChecked === "true",
      );
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener("change", this.variantChanged.bind(this));
    this.addEventListener("mousedown", this.#blockOOSClick.bind(this), true);
    this.addEventListener("click", this.#handleMissingComboClick.bind(this));
    this.#resizeObserver.observe(this);
    requestAnimationFrame(() => this.#markMissingCombinations());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    if (this.#searchPopup) {
      this.#searchPopup.remove();
      this.#searchPopup = null;
    }
  }

  /**
   * Returns the variant data from the embedded JSON script tag.
   * Each entry has `options` (array of option values) and `available` (boolean).
   * @returns {{ options: string[], available: boolean }[]}
   */
  get #productVariants() {
    const script = this.querySelector("script[data-product-variants]");
    if (!script?.textContent) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  /**
   * Marks every visible button as either a real OOS variant (data-combination-missing="false")
   * or a combination that simply doesn't exist for the current selection (data-combination-missing="true").
   * All buttons stay visible — missing ones get a distinct "search" design + click-to-find popup.
   */
  #markMissingCombinations() {
    const variants = this.#productVariants;
    if (!variants.length) return;

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );

    const selectedValues = fieldsets.map((fieldset) => {
      const checked = /** @type {HTMLInputElement | null} */ (
        fieldset.querySelector("input:checked")
      );
      return checked ? checked.value : null;
    });

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      fieldset
        .querySelectorAll("label.variant-option__button-label")
        .forEach((rawLabel) => {
          const label = /** @type {HTMLElement} */ (rawLabel);
          const input = label.querySelector("input");
          if (!input) return;

          const inputValue = input.value;

          // Does ANY variant (in or out of stock) match this option value + earlier selections?
          const comboExists = variants.some(({ options }) =>
            options.every((optionValue, optIndex) => {
              if (optIndex === fieldsetIndex) return optionValue === inputValue;
              if (optIndex > fieldsetIndex) return true;
              const otherSelected = selectedValues[optIndex];
              return otherSelected == null || optionValue === otherSelected;
            }),
          );

          label.dataset.combinationMissing = comboExists ? "false" : "true";
          label.hidden = false; // always show — no hiding

          // Inject / remove corner badge
          const existingBadge = label.querySelector(
            ".variant-option__missing-badge",
          );
          if (!comboExists) {
            if (!existingBadge) {
              const badge = document.createElement("span");
              badge.className = "variant-option__missing-badge";
              badge.setAttribute("aria-hidden", "true");
              label.appendChild(badge);
            }
          } else {
            existingBadge?.remove();
          }
        });
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.#markMissingCombinations();
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      }),
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(
      this.closest("featured-product-information"),
    );

    const morphElementSelector = loadsNewProduct
      ? "main"
      : isOnFeaturedProductSection
        ? "featured-product-information"
        : undefined;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      morphElementSelector,
    );

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth:
        currentIndex !== undefined
          ? radios[currentIndex]?.parentElement?.offsetWidth
          : undefined,
      previousWidth:
        previousIndex !== undefined
          ? radios[previousIndex]?.parentElement?.offsetWidth
          : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({
    fieldset,
    currentWidth,
    previousWidth,
    currentIndex,
    previousIndex,
  }) {
    if (currentWidth) {
      fieldset.style.setProperty("--pill-width-current", `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty("--pill-width-current");
    }

    if (previousWidth) {
      fieldset.style.setProperty("--pill-width-previous", `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty("--pill-width-previous");
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`,
      );

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || "");
      const inputIndex = Number.parseInt(target.dataset.inputIndex || "");

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
          this.refs.fieldsets || []
        );
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = "false";
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = "false";
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = "true";
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = "true";
            radios[newPreviousIndex].dataset.currentChecked = "false";
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      "quick-add-component": "section-rendering-product-card",
      "swatches-variant-picker-component": "section-rendering-product-card",
      "featured-product-information": this.closest(
        "featured-product-information",
      )?.id,
    };

    const closestSectionId =
      /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
        Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
      );

    if (closestSectionId) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join("&")}`;
    }

    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`,
        )?.textContent;
        if (!textContent) return;

        if (morphElementSelector === "main") {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(
                JSON.parse(textContent),
                this.selectedOptionId,
                {
                  html,
                  productId: this.dataset.productId ?? "",
                  newProduct,
                },
              ),
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase(),
    );

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();
    this.#markMissingCombinations();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets
      .map((_, index) => this.#getFieldsetMeasurements(index))
      .filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      "select option[selected], fieldset input:checked",
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }

  // ─── OOS hover tooltip ─────────────────────────────────────────────────────

  /**
   * Returns the currently selected value for each fieldset, in order.
   * @returns {(string | null)[]}
   */
  #getSelectedValues() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );
    return fieldsets.map((fieldset) => {
      const checked = /** @type {HTMLInputElement | null} */ (
        fieldset.querySelector("input:checked")
      );
      return checked?.value ?? null;
    });
  }

  /**
   * Programmatically applies an alternative variant by clicking each option's radio.
   * @param {string[]} options
   */
  #applyAlternative(options) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );
    options.forEach((value, index) => {
      const fieldset = fieldsets[index];
      if (!fieldset) return;
      const input = Array.from(fieldset.querySelectorAll("input")).find(
        (i) => i.value === value,
      );
      if (input && !input.checked) input.click();
    });
  }

  /**
   * Prevents mousedown on OOS labels so the radio cannot be checked.
   * Missing-combination labels are allowed through (they show the search popup instead).
   * @param {Event} event
   */
  #blockOOSClick(event) {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest("label.variant-option__button-label");
    if (!label) return;
    if (
      /** @type {HTMLElement} */ (label).dataset.combinationMissing === "true"
    )
      return;
    if (label.querySelector('input[data-option-available="false"]')) {
      event.preventDefault();
    }
  }

  /**
   * Click handler for missing-combination labels — opens the full-page modal.
   * @param {Event} event
   */
  #handleMissingComboClick(event) {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest("label.variant-option__button-label");
    if (!(label instanceof HTMLElement)) return;
    if (label.dataset.combinationMissing !== "true") return;

    const input = /** @type {HTMLInputElement | null} */ (
      label.querySelector("input")
    );
    if (!input) return;

    event.preventDefault();
    this.#openSizeModal(
      input.value,
      parseInt(input.dataset.fieldsetIndex ?? "-1", 10),
    );
  }

  /**
   * Opens the full-page modal for a missing size.
   * Within-product alternatives render instantly; cross-product search loads async.
   * @param {string} sizeValue
   * @param {number} fieldsetIndex
   */
  /** @returns {{ lookingFor: string, sizeLabel: string, availableInProduct: string, availableInOther: string, noAlternatives: string, close: string }} */
  get #i18n() {
    return {
      lookingFor: this.dataset.i18nLookingFor || "Looking for",
      sizeLabel: this.dataset.i18nSizeLabel || "Size",
      availableInProduct:
        this.dataset.i18nAvailableInProduct || "Available in this product",
      availableInOther:
        this.dataset.i18nAvailableInOther || "Available in other collections",
      noAlternatives:
        this.dataset.i18nNoAlternatives || "No alternatives found",
      close: this.dataset.i18nClose || "Close",
    };
  }

  #openSizeModal(sizeValue, fieldsetIndex) {
    const modal = this.#getOrCreateModal();
    const currentValues = this.#getSelectedValues();

    // ── Detect color option ────────────────────────────────────────────────────
    // Prefer the index pre-computed in Liquid (works for any language).
    // Fall back to legend-text sniffing for safety.
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );
    let colorValue = "";
    let colorIndex = -1;
    const colorIndexFromData = parseInt(
      this.dataset.colorOptionIndex ?? "-1",
      10,
    );
    if (colorIndexFromData !== -1 && colorIndexFromData !== fieldsetIndex) {
      colorIndex = colorIndexFromData;
      colorValue = currentValues[colorIndex] ?? "";
    } else {
      fieldsets.forEach((fieldset, i) => {
        if (i === fieldsetIndex) return;
        const legendText =
          fieldset.querySelector("legend")?.textContent?.trim().toLowerCase() ??
          "";
        if (
          legendText.includes("color") ||
          legendText.includes("colour") ||
          legendText.includes("لون") ||
          legendText.includes("اللون")
        ) {
          colorValue = currentValues[i] ?? "";
          colorIndex = i;
        }
      });
    }

    // ── Update title ──────────────────────────────────────────────────────────
    const i18n = this.#i18n;
    const eyebrowEl = modal.querySelector(".vp-modal__eyebrow");
    if (eyebrowEl) eyebrowEl.textContent = i18n.lookingFor;
    const sizeLabelEl = modal.querySelector(".vp-modal__title-label");
    if (sizeLabelEl) sizeLabelEl.textContent = i18n.sizeLabel + " ";
    const titleEl = modal.querySelector(".vp-modal__title-size");
    if (titleEl) {
      titleEl.textContent = colorValue
        ? `${sizeValue} – ${colorValue}`
        : sizeValue;
    }
    const inProductLabelEl = modal.querySelector(
      ".vp-modal__section-label--in-product",
    );
    if (inProductLabelEl)
      inProductLabelEl.textContent = i18n.availableInProduct;
    const inOtherLabelEl = modal.querySelector(
      ".vp-modal__section-label--in-other",
    );
    if (inOtherLabelEl) inOtherLabelEl.textContent = i18n.availableInOther;

    // ── Within-product section: same color + same size → available fits ────────
    const sameColorSizeAlts = this.#productVariants.filter(
      (v) =>
        v.available &&
        v.options[fieldsetIndex] === sizeValue &&
        (colorIndex === -1 || v.options[colorIndex] === colorValue),
    );

    let instantHtml = "";
    if (sameColorSizeAlts.length) {
      const chips = sameColorSizeAlts
        .map((v) => {
          // Show only the fit name (exclude size and color dimensions)
          const fitLabel =
            v.options
              .filter((_, i) => i !== fieldsetIndex && i !== colorIndex)
              .join(" · ") || v.options.join(" · ");
          const encoded = encodeURIComponent(JSON.stringify(v.options));
          return `<button type="button" class="vp-modal__chip" data-options="${encoded}">${fitLabel}</button>`;
        })
        .join("");
      instantHtml = `<div class="vp-modal__chips">${chips}</div>`;
    } else {
      instantHtml = `<p class="vp-modal__none">${i18n.noAlternatives}</p>`;
    }

    const instantEl = modal.querySelector(".vp-modal__instant-body");
    if (instantEl) instantEl.innerHTML = instantHtml;

    const asyncEl = modal.querySelector(".vp-modal__async-body");
    if (asyncEl)
      asyncEl.innerHTML = '<span class="vp-modal__loading-bar"></span>';

    modal.removeAttribute("hidden");
    document.body.style.overflow = "hidden";

    // Wire all chip clicks (instant fits + other colors)
    modal.querySelectorAll(".vp-modal__chip[data-options]").forEach((btn) => {
      btn.addEventListener(
        "click",
        () => {
          try {
            const opts = JSON.parse(
              decodeURIComponent(
                /** @type {HTMLElement} */ (btn).dataset.options ?? "[]",
              ),
            );
            this.#applyAlternative(opts);
          } catch {}
          this.#closeModal();
        },
        { once: true },
      );
    });

    // ── Cross-product section (async) ──────────────────────────────────────────
    // Detect the active locale prefix so we can localise product URLs and titles.
    const localeMatch = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\//i);
    const localePrefix = localeMatch ? `/${localeMatch[1]}` : '';

    this.#fetchProductsWithSize(
      sizeValue,
      colorValue,
      colorIndex,
      this.dataset.productType ?? "",
    ).then(async (products) => {
      if (!asyncEl) return;

      if (!products.length) {
        asyncEl.innerHTML = `<p class="vp-modal__none">${i18n.noAlternatives}</p>`;
        return;
      }

      // If we're in a localised storefront, fetch each product's translated title
      // in parallel. The search always runs against the English index so titles
      // come back in English; we swap them for the active-locale version here.
      const displayProducts = localePrefix
        ? await Promise.all(
            products.map(async (p) => {
              const handle = (p.url ?? '').match(/\/products\/([^/?]+)/)?.[1];
              if (!handle) return p;
              try {
                const r = await fetch(`${localePrefix}/products/${handle}.json`);
                if (!r.ok) return p;
                const d = await r.json();
                return { ...p, title: d.product?.title ?? p.title };
              } catch { return p; }
            }),
          )
        : products;

      asyncEl.innerHTML = displayProducts
        .map((p) => {
          const imgUrl = p.image ?? p.featured_image?.url ?? "";
          const rawUrl = p.variants?.[0]?.url ?? p.url ?? "";
          const urlObj = new URL(rawUrl, window.location.origin);
          ["_pos", "_psq", "_ss", "_v"].forEach((k) => urlObj.searchParams.delete(k));
          // Strip any existing locale prefix from the path then prepend the active one
          const cleanPath = urlObj.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?\//i, '/');
          const variantUrl = localePrefix + cleanPath + (urlObj.search || "");
          return (
            `<a class="vp-modal__product-card" href="${variantUrl}">` +
            (imgUrl
              ? `<img src="${imgUrl}" alt="${p.title}" class="vp-modal__card-img" loading="lazy">`
              : `<span class="vp-modal__card-img vp-modal__card-img--placeholder"></span>`) +
            `<span class="vp-modal__card-name">${p.title}</span>` +
            `</a>`
          );
        })
        .join("");
    });
  }

  /** Closes the modal and restores scroll. */
  #closeModal() {
    if (this.#searchPopup) this.#searchPopup.setAttribute("hidden", "");
    document.body.style.overflow = "";
  }

  /** @returns {HTMLElement} */
  #getOrCreateModal() {
    if (!this.#searchPopup) {
      const i18n = this.#i18n;
      const dir = document.documentElement.getAttribute("dir") || "ltr";
      const el = document.createElement("div");
      el.className = "vp-modal";
      el.setAttribute("hidden", "");
      el.setAttribute("role", "dialog");
      el.setAttribute("aria-modal", "true");
      el.setAttribute("dir", dir);
      el.innerHTML = `
        <div class="vp-modal__backdrop"></div>
        <div class="vp-modal__panel">
          <div class="vp-modal__header">
            <div>
              <p class="vp-modal__eyebrow">${i18n.lookingFor}</p>
              <h2 class="vp-modal__title"><span class="vp-modal__title-label">${i18n.sizeLabel} </span><span class="vp-modal__title-size"></span></h2>
            </div>
            <button type="button" class="vp-modal__close" aria-label="${i18n.close}">
              <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <div class="vp-modal__body">
            <section class="vp-modal__section">
              <p class="vp-modal__section-label vp-modal__section-label--in-product">${i18n.availableInProduct}</p>
              <div class="vp-modal__instant-body"></div>
            </section>
            <section class="vp-modal__section">
              <p class="vp-modal__section-label vp-modal__section-label--in-other">${i18n.availableInOther}</p>
              <div class="vp-modal__async-body vp-modal__async-products"></div>
            </section>
          </div>
        </div>`;
      document.body.appendChild(el);

      el.querySelector(".vp-modal__backdrop")?.addEventListener("click", () =>
        this.#closeModal(),
      );
      el.querySelector(".vp-modal__close")?.addEventListener("click", () =>
        this.#closeModal(),
      );
      document.addEventListener("keydown", (e) => {
        if (
          e.key === "Escape" &&
          this.#searchPopup &&
          !this.#searchPopup.hidden
        )
          this.#closeModal();
      });

      this.#searchPopup = el;
    }
    return this.#searchPopup;
  }

  /**
   * Fetches /products/handle.json (default locale, always English) once per instance.
   * Returns a color map (localized → English) and the English product type string.
   * @param {number} colorIndex
   * @returns {Promise<{colorMap: Map<string,string>, productType: string}>}
   */
  #getEnglishProductData(colorIndex) {
    if (this.#englishProductDataPromise) return this.#englishProductDataPromise;

    this.#englishProductDataPromise = (async () => {
      const colorMap = new Map();
      let productType = '';
      try {
        // Strip any locale prefix so we always hit the default-language endpoint
        const rawUrl = this.dataset.productUrl ?? '';
        const cleanPath = rawUrl.replace(/^\/[a-z]{2}(-[a-z]{2})?\//i, '/');
        const handle = cleanPath.match(/\/products\/([^/?]+)/)?.[1];
        if (!handle) return { colorMap, productType };

        const res = await fetch(`/products/${handle}.json`);
        if (!res.ok) return { colorMap, productType };
        const data = await res.json();

        productType = data.product?.product_type ?? '';

        const englishColorValues = /** @type {string[]} */ (
          data.product?.options?.[colorIndex]?.values ?? []
        );

        // Collect unique localized color values in the same positional order
        // as they appear in #productVariants (rendered in the active locale).
        const seen = new Set();
        const localizedValues = [];
        for (const v of this.#productVariants) {
          const c = v.options[colorIndex];
          if (c !== undefined && !seen.has(c)) { seen.add(c); localizedValues.push(c); }
        }

        localizedValues.forEach((locVal, i) => {
          if (englishColorValues[i]) colorMap.set(locVal, englishColorValues[i]);
        });
      } catch { /* return empty defaults */ }
      return { colorMap, productType };
    })();

    return this.#englishProductDataPromise;
  }

  async #fetchProductsWithSize(sizeValue, colorValue, colorIndex, productType) {
    try {
      // Fetch English product data once (color map + English product type).
      const { colorMap, productType: englishType } = await this.#getEnglishProductData(colorIndex);

      const searchColor = (colorIndex !== -1 && colorValue)
        ? (colorMap.get(colorValue) ?? colorValue)
        : colorValue;

      const searchType = englishType || productType;

      // Determine which colors are acceptable: exact match only, unless the color
      // belongs to a substitution group (e.g. all blues, charcoal+anthracite, etc.)
      const acceptableColors = VariantPicker.#getAcceptableColors(searchColor);

      const stripLocale = (/** @type {string} */ p) =>
        p.replace(/^\/[a-z]{2}(-[a-z]{2})?\//i, '/');
      const currentPath = stripLocale(window.location.pathname.split('?')[0]);

      // Run one search per acceptable color so each exact name is in the query.
      // This casts the widest net while staying close to the desired color.
      // Results from all queries are merged and deduplicated by product URL.
      const colorsToSearch = acceptableColors.length > 0 ? acceptableColors : [searchColor ?? ''];
      const seenPaths = new Set();
      const candidates = /** @type {any[]} */ ([]);

      await Promise.all(
        colorsToSearch.map(async (color) => {
          const query = [sizeValue, color].filter(Boolean).join(' ');
          const params = new URLSearchParams({
            q: query,
            'resources[type]': 'product',
            'resources[limit]': '10',
          });
          // Always use the root (default-locale) search endpoint.
          const res = await fetch(`/search/suggest.json?${params}`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!res.ok) return;
          const data = await res.json();
          const results = /** @type {any[]} */ (data.resources?.results?.products ?? []);
          for (const p of results) {
            const path = stripLocale((p.url ?? '').split('?')[0]);
            if (path === currentPath) continue;
            if (searchType && (p.type ?? p.product_type ?? '').toLowerCase() !== searchType.toLowerCase()) continue;
            if (seenPaths.has(path)) continue;
            seenPaths.add(path);
            candidates.push(p);
          }
        }),
      );

      // Post-verify each candidate: fetch its product JSON and confirm it has a
      // variant whose option values contain an exact match for one of the
      // acceptable colors. This eliminates substring false-positives (e.g. a
      // search for "Black" returning a product that only has "Blue Black").
      const verified = (
        await Promise.all(
          candidates.slice(0, 12).map(async (p) => {
            const handle = (p.url ?? '').match(/\/products\/([^/?]+)/)?.[1];
            if (!handle) return null;
            try {
              const res = await fetch(`/products/${handle}.json`);
              if (!res.ok) return null;
              const data = await res.json();
              // Collect all option values from every variant (flat, lowercase).
              // Shopify REST API uses option1/option2/option3 — NOT an options array.
              const allOptionValues = /** @type {string[]} */ (
                (data.product?.variants ?? [])
                  .flatMap((/** @type {any} */ v) => [v.option1, v.option2, v.option3].filter(Boolean))
                  .map((/** @type {string} */ o) => o.toLowerCase())
              );
              const hasExactColor = allOptionValues.some(o => acceptableColors.includes(o));
              return hasExactColor ? p : null;
            } catch {
              return null;
            }
          }),
        )
      ).filter(Boolean);

      return verified.slice(0, 6);
    } catch {
      return [];
    }
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
